import { useMemo, useState, useRef, useEffect, forwardRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { pageTitle } from "../lib/mcmTokens";
import { useTheme as useMCMTheme } from "../lib/theme";
import { useBadges } from "../lib/useBadges";
import { pickGreeting } from "../lib/greetings";
import { LEVEL_OPTIONS, GOAL_OPTIONS, DIETARY_OPTIONS } from "../data";
import DailyRollCard from "./DailyRollCard";
import FamilyFavoritesCard from "./FamilyFavoritesCard";
import FlairHalo, { isFlairActive } from "./FlairHalo";
import RadioToggle from "./RadioToggle";
import FamilyKitchenView from "./FamilyKitchenView";
import { playSfx } from "../lib/sfx";
import MCMBigCard from "./MCMBigCard";
import { useFocusContext } from "../lib/useFocusContext";
import { useHomeSnapshot } from "../lib/useHomeSnapshot";
import { useUserRecipes } from "../lib/useUserRecipes";
import GetStartedCard from "./GetStartedCard";
import { findIngredient } from "../data/ingredients";
import { findRecipe } from "../data/recipes";
import { prettifySlug } from "../lib/calendarUtils";
import { computeRecipeMatch } from "../lib/recipePairing";
import { recipeAllergyConflicts } from "../data/allergens";
import { useRecommendedStubs } from "../lib/useRecommendedStubs";
import { cookStub } from "../lib/cookStub";

// Rating face lookup shared with the activity feed's cook rows.
const RATING_EMOJI = { nailed: "🤩", good: "😊", meh: "😐", rough: "😬" };

// "5m ago", "3h ago", "2d ago" — shared relative formatter.
function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

// Freshness window for the soft gold-glow on snapshot rows. 5 minutes
// is wide enough that a row stays "alive-looking" while the user is
// still on the page after triggering the action, and narrow enough
// that the glow is rare on a normal Home open. No animation — these
// rows are seen tens of times/day, so a static glow is the right
// signal per the animation framework.
const FRESH_MS = 5 * 60 * 1000;
function isFresh(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < FRESH_MS;
}
const FRESH_ROW_OVERLAY = {
  borderColor: "rgba(245,200,66,0.45)",
  boxShadow: "0 0 0 1px rgba(245,200,66,0.25), 0 0 18px rgba(245,200,66,0.14)",
};

// Snapshot row tile style — exact match for the kitchen-tile look
// (TileGrid.jsx). Reads live theme tokens so the rows track the
// time-of-day theme just like the kitchen grid does:
//   - glassFillLite (LIGHTER cream alpha so the page bg bleeds
//     through, same as kitchen tiles — not the heavier 0.75 token)
//   - glassBorder (bright 0.85-alpha cream highlight, not the dark
//     hairline) — gives the lit-edge effect
//   - blur(14px) — same as TileCard's stronger blur
//   - radius.xl (24)
//   - theme.shadow.glass — composed drop + inset stack with the
//     bright top-edge highlight + warm bottom-edge shadow
function useTileRowStyle() {
  const { theme } = useMCMTheme();
  return useMemo(() => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: theme.color.glassFillLite,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: `1px solid ${theme.color.glassBorder}`,
    borderRadius: 24,
    boxShadow: theme.shadow?.glass,
    padding: "14px 16px",
  }), [theme]);
}

// Theme-aware text colors for the snapshot rows. Without this, the
// static COLOR.ink token bakes a dark text color into rows whose
// background flips to a dark glass on night/dawn/dusk themes —
// rendering the title illegible. Same auto-contrast pattern the page
// title already uses (skyInk wins on sky-tinted themes; falls back
// to neutral ink on cream surfaces).
function useTileRowTextStyles() {
  const { theme } = useMCMTheme();
  return useMemo(() => {
    const ink      = theme.color.skyInk      || theme.color.ink;
    const inkMuted = theme.color.skyInkMuted || theme.color.inkMuted || COLOR.muted;
    return {
      narration: {
        fontFamily: FONT.forma,
        fontSize: 14,
        color: ink,
        lineHeight: 1.35,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      itemInline: {
        fontFamily: FONT.itemTitle,
        fontStyle: "italic",
        fontWeight: 500,
        color: ink,
      },
      meta: {
        fontFamily: FONT.forma,
        fontSize: 11,
        color: inkMuted,
        marginTop: 3,
      },
    };
  }, [theme]);
}

// Deterministic avatar color keyed on name — matches the palette used in
// Settings/UserProfile so Marissa's tile is always the same shade.
function avatarColor(name) {
  if (!name) return COLOR.goldDeep;
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const palette = [COLOR.goldDeep, "#15201a", "#1a1228", "#2a1a15", COLOR.skyDeep, "#2a1e28"];
  return palette[Math.abs(hash) % palette.length];
}

/**
 * Home — recommendation-first. Bands top-to-bottom:
 *
 *   1. Top bar — streak chip + 48px avatar (primary profile entry).
 *   2. Greeting — Pale Martini display, rolled per-mount.
 *   3. FOCUS CARD — single contextual recommendation derived from
 *      useFocusContext. Priority: active cook → upcoming meal →
 *      expiring item → AI suggestion. Always present.
 *   4. Daily Roll — kept as a charm.
 *   5. SNAPSHOT PANEL — swipeable 4-page surface (Cooks · Kitchen ·
 *      Recipes · Calendar) showing top 3 of each + "Show more".
 *      Reads from useHomeSnapshot.
 *
 * The old single time-ordered activity feed and the diet/level/goal
 * ProfilePill are removed — the focus card answers "what now?" and
 * the snapshot panel answers "what's happening?" without flooding
 * the page with one mixed scroll.
 */
export default function Home({
  profile, userId, familyIds = [], familyLoading = false, nameFor, avatarFor,
  openProfile, openCook, onScheduledTap,
  // Recommendation inputs (passed from App so we don't double-mount
  // hooks: pantry comes from useSyncedList in App).
  pantry = [],
  // Tap actions for the Focus Card branches. Resume-cook lives on
  // CookBanner (App-level) — it's not a Focus Card tier.
  onOpenAIRecipe, onOpenCalendar, onOpenKitchen, onRestock,
  // Get-started checklist inputs (new-user pipeline).
  connectionsCount = 0, onOpenFindFriends,
  // Notifications — bell lives in Home's top bar (moved from
  // UserProfile so unread state is visible without a tap).
  onOpenNotifs, notifsUnread = 0,
  // Family Kitchen — when present, the Cooks sub-tab renders the
  // conductor view (active cooks + planned meals + plan-a-meal) at
  // the top, with past history below. Omitting these falls back to
  // history-only behavior. (Note: findUserRecipe isn't passed in
  // separately — Home already derives it from useUserRecipes below
  // and reuses that for both the snapshot and the Family Kitchen.)
  activeCooks = [],
  mealPlans = [],
  fireMealPlanRecipe = null,
  onJumpToCook = null,
  onOpenMealPlanBuilder = null,
}) {
  // MCM theme — drives auto-contrast on the hero greeting so the text
  // stays readable across morning / day / dusk / evening / night.
  const { theme: mcmTheme } = useMCMTheme();

  // Greeting is picked once per mount — re-tabbing back to Home rolls
  // again, which is the whole point (easter eggs should feel like a
  // surprise, not a setting). useMemo with userId in the deps keeps it
  // stable for the rest of the render pass. onShow side effects (like
  // the Idiot Sandwich mint) fire inside pickGreeting the first time
  // they surface for a given user, module-guarded against re-fires.
  const greeting = useMemo(
    () => pickGreeting({ name: profile.name, userId }),
    [profile.name, userId]
  );

  // Badges catalog powers the snapshot panel's cook-row badge meta
  // (snippet rendering still resolves badge ids when the cook row
  // includes one — kept the catalog hook for that lookup).
  const badges = useBadges(userId);

  // Resolve a badge id → catalog row for any inline badge rendering.
  const badgeById = useMemo(() => {
    const m = new Map();
    for (const b of badges.catalog) m.set(b.id, b);
    return m;
  }, [badges.catalog]);

  // Recommendation engine — what should the user do right now?
  const focus = useFocusContext({ userId, profile, pantry });

  // Categorized snapshot of self + family activity.
  const snapshot = useHomeSnapshot({ userId, familyIds });
  // Recipe-title resolver for the calendar list. scheduled_meals only
  // stores recipe_slug; we read user_recipes via the cached hook so
  // the row can render "You scheduled Brioche" instead of just "Meal".
  const { recipes: userRecipesAll, findBySlug: findUserRecipe } = useUserRecipes(userId);

  // Get-started checklist signals — the viewer's OWN recipe rows
  // (the hook returns self + family) and their cuisine spread.
  const ownRecipes = (userRecipesAll || []).filter((r) => r.userId === userId);
  const ownCuisineCount = new Set(
    ownRecipes.map((r) => (r.recipe?.cuisine || "").toLowerCase()).filter(Boolean),
  ).size;

  const openSelf = () => openProfile?.(userId);

  return (
    <div style={{ minHeight: "100vh", paddingBottom: 100 }}>
      {/* Header row — page title left, bell + avatar right. Title
          sits at the same vertical position as Courses / Plan /
          Kitchen so all tabs share one masthead height. The icons
          align to the title's baseline-ish center via flex. */}
      <div style={{
        padding: "calc(24px + env(safe-area-inset-top)) 20px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}>
        <h1 style={{
          ...pageTitle,
          color: mcmTheme.color.skyInk || mcmTheme.color.ink,
          flex: "0 1 auto",
          minWidth: 0,
        }}>
          Home
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          {/* Radio toggle — mute/unmute the in-app music + ad
              rotation. Sits leftmost in the top-right cluster so
              it's easy to thumb without grazing the bell or avatar
              by accident. */}
          <RadioToggle />
          {/* Notifications bell — custom PNG icon, no chip
              background. Sized to match the avatar (48px) so the
              two read as a pair. */}
          {onOpenNotifs && (
            <button
              type="button"
              onClick={() => { playSfx("notification-bell"); onOpenNotifs(); }}
              title="Notifications"
              aria-label="Notifications"
              style={{
                position: "relative",
                background: "transparent",
                border: "none",
                padding: 0,
                width: 48,
                height: 48,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <img
                src="/img/notification-bell.png"
                alt=""
                style={{ width: 48, height: 48, objectFit: "contain", display: "block" }}
              />
              {notifsUnread > 0 && (
                <span style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  minWidth: 16,
                  height: 16,
                  padding: "0 4px",
                  borderRadius: 8,
                  background: COLOR.gold,
                  color: COLOR.night,
                  fontFamily: FONT.mono,
                  fontSize: 10,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  {notifsUnread > 99 ? "99+" : notifsUnread}
                </span>
              )}
            </button>
          )}
          {/* Profile avatar — primary visual entry point to the
              user's own profile from Home. */}
          <FlairHalo active={isFlairActive(profile)} size={48}>
            <Avatar
              name={profile.name}
              initial={(profile.name || "?")[0]?.toUpperCase() || "?"}
              imageUrl={profile.avatar_url}
              onClick={openSelf}
            />
          </FlairHalo>
        </div>
      </div>

      {/* Greeting — New Kansas italic, the human-voice secondary
          line under the Martini title. Rolled per-mount; tier tag
          tints rare/ultra/disney/mythic variants. */}
      <div style={{ padding: "8px 20px 0" }}>
        <h2 style={{
          fontFamily: FONT.itemTitle,
          fontSize: 26,
          fontWeight: 500,
          fontStyle: "italic",
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
          margin: 0,
          color: greeting.tier === "mythic" || greeting.tier === "ultra" || greeting.tier === "rare"
            ? COLOR.gold
            : greeting.tier === "disney"
              ? COLOR.amber
              : (mcmTheme.color.skyInk || mcmTheme.color.ink),
          textShadow: greeting.tier === "mythic"
            ? "0 0 40px #f5c84266, 0 2px 6px rgba(60,30,10,0.45)"
            : greeting.tier === "ultra"
              ? "0 0 24px #f5c84244, 0 2px 6px rgba(60,30,10,0.45)"
              : "0 1px 3px rgba(60,30,10,0.35)",
        }}>
          {greeting.text}
        </h2>
      </div>

      {/* Get-started checklist — new-user pipeline's landing state.
          Self-gating (onboarded_at + not dismissed + not all-done);
          rows track real signals and tap through to the surface that
          completes them. */}
      <GetStartedCard
        userId={userId}
        profile={profile}
        pantryCount={(pantry || []).length}
        ownRecipeCount={ownRecipes.length}
        ownCuisineCount={ownCuisineCount}
        connectionsCount={connectionsCount}
        onOpenKitchen={onOpenKitchen}
        onOpenCreate={onOpenAIRecipe}
        onOpenFindFriends={onOpenFindFriends}
      />

      {/* Focus Carousel — every valid nudge from useFocusContext as
          a swipeable + auto-rotating page. Lets the user see ALL
          pending things to do, not just the most pressing one. */}
      <div style={{ padding: "20px 20px 0" }}>
        <FocusCarousel
          tiers={focus?.tiers || []}
          onOpenAIRecipe={onOpenAIRecipe}
          onOpenCalendar={onOpenCalendar}
          onOpenKitchen={onOpenKitchen}
          onRestock={onRestock}
        />
      </div>

      {/* Daily scratch-card roll — kept as a charm. */}
      <DailyRollCard profile={profile} />

      {/* Family favorites — the household's top dishes from the last
          90 days (community-leaderboard scoring over family-visible
          cook_logs). Silent for family-less users and until at least
          one dish has been cooked twice. */}
      <FamilyFavoritesCard
        userId={userId}
        familyIds={familyIds}
        nameFor={nameFor}
        resolveRecipe={(slug) => findRecipe(slug, findUserRecipe)}
        onCookRecipe={onJumpToCook}
      />

      {/* Ready-to-cook banner — surfaces a recipe the user can make
          RIGHT NOW from current pantry. Silent when nothing's ready.
          Rotates per mount (re-tabbing back to Home picks fresh).
          Suppressed entirely in pantry-off mode (kitchen_tracking
          false) — "cook from what you have" is noise for someone who
          deliberately doesn't track what they have. */}
      {profile?.kitchen_tracking !== false && (
        <ReadyToCookBanner
          userId={userId}
          pantry={pantry}
          allergies={Array.isArray(profile?.allergies) ? profile.allergies : []}
          onCookRecipe={onJumpToCook}
        />
      )}

      {/* Live cooks: lock-screen-shape card per active cook. Renders
          ABOVE the snapshot panel only when something is actively
          cooking, so the user sees the live state without having to
          tap into a sub-tab. Tap a card → opens that cook's CookMode. */}
      {Array.isArray(activeCooks) && activeCooks.length > 0 ? (
        <div style={{ margin: "20px 16px 8px" }}>
          <FamilyKitchenView
            cooks={activeCooks}
            currentUserId={userId}
            avatarFor={avatarFor}
            onJumpToCook={onJumpToCook}
          />
        </div>
      ) : null}

      {/* Snapshot panel — swipeable 4-page snapshot of self+family
          activity. Segmented control + framer-friendly horizontal
          scroller, top 3 per category + show more. */}
      <SnapshotPanel
        snapshot={snapshot}
        familyLoading={familyLoading}
        familyIds={familyIds}
        nameFor={nameFor}
        avatarFor={avatarFor}
        viewerId={userId}
        findUserRecipe={findUserRecipe}
        onCookTap={openCook}
        onActorTap={openProfile}
        onOpenKitchen={onOpenKitchen}
        onOpenCalendar={onOpenCalendar}
        onScheduledTap={onScheduledTap}
        activeCooks={activeCooks}
        mealPlans={mealPlans}
        fireMealPlanRecipe={fireMealPlanRecipe}
        onJumpToCook={onJumpToCook}
        onOpenMealPlanBuilder={onOpenMealPlanBuilder}
      />

    </div>
  );
}

// ── Ready-to-cook banner ─────────────────────────────────────────────────────
//
// Surfaces ONE recipe the user can fully cook from their current
// pantry, with a tap-to-cook CTA. Silent (renders null) when nothing
// is ready — no clutter when the pantry can't make anything.
//
// Selection: every render mount picks a single ready recipe to
// feature. The selection is stable across re-renders within a mount
// (memoized on the ready set's stable shape), so the banner doesn't
// flicker between options. Re-tabbing back to Home re-mounts the
// component and rolls a fresh pick — gives the feeling of "rotating
// suggestions" without any persistent state.
//
// Match logic: computeRecipeMatch from src/lib/recipePairing.js does
// the per-ingredient pair walk and rolls up. `ready` means every
// recipe ingredient has a pantry match (paired, substitute, or
// wrong-state — anything but "missing"). Wrong-state and substitute
// count as ready because the user can convert / swap at cook time.
// Cycle every N ms when multiple announcements are stacked. Long
// enough to read comfortably, short enough that the rotation feels
// alive when the user lingers on Home. Paused while a stub-build
// is in flight so the user isn't whiplashed mid-action.
const ANNOUNCEMENT_CYCLE_MS = 5500;

function ReadyToCookBanner({ userId, pantry, allergies = [], onCookRecipe }) {
  const { recipes, loading, saveRecipe } = useUserRecipes(userId);
  const [building, setBuilding] = useState(null);  // { stubId, title } | null
  const [currentIndex, setCurrentIndex] = useState(0);

  // Roll up ALL ready recipes from the user's book against the
  // current pantry. Keyed on the recipes ref + a coarse pantry
  // signature so this doesn't re-run on every micro pantry mutation.
  const readyList = useMemo(() => {
    if (loading) return [];
    if (!Array.isArray(recipes) || recipes.length === 0) return [];
    if (!Array.isArray(pantry) || pantry.length === 0) return [];
    const out = [];
    for (const row of recipes) {
      const r = row?.recipe;
      if (!r || !r.title || !Array.isArray(r.ingredients) || r.ingredients.length === 0) continue;
      try {
        // SAFETY: hard-exclude, same rule as the stub path below. A
        // saved-for-someone-else recipe (peanut satay for a family
        // member) must never be ANNOUNCED to the allergic viewer —
        // it stays reachable in the book, chip-flagged.
        if (Array.isArray(allergies) && allergies.length > 0 &&
            recipeAllergyConflicts(r, allergies).length > 0) continue;
        const match = computeRecipeMatch(r, pantry);
        if (match.ready) out.push({ recipe: r });
      } catch (_) { /* skip on pairing error */ }
    }
    return out;
  }, [recipes, pantry, loading, allergies]);

  // Stub recommendations — occasion-aware. Always consulted now;
  // stubs interleave with ready user recipes in the cycle.
  const { stubs: recommendedStubs } = useRecommendedStubs({
    pantry,
    userRecipes: recipes,
    limit: 5,
    // SAFETY: never auto-recommend a recipe the viewer is allergic to.
    allergies,
  });

  // Build the unified announcement deck. Ready user recipes come
  // first (strongest signal — "you literally have everything"), then
  // stubs (discovery — "you might want to make this"). Order is
  // stable so cycling is predictable; the first-render index is
  // randomized so reopening Home doesn't always start with the same
  // headline.
  const announcements = useMemo(() => {
    const list = [];
    for (const ready of readyList) {
      list.push({ key: `user:${ready.recipe.slug || ready.recipe.title}`,
        kind: "user-recipe",
        recipe: ready.recipe });
    }
    for (const entry of (recommendedStubs || [])) {
      list.push({ key: `stub:${entry.stub.id}`,
        kind: "stub",
        stub: entry.stub,
        occasion: entry.occasions?.[0] || null });
    }
    return list;
  }, [readyList, recommendedStubs]);

  // Randomize the starting position on mount so the rotation
  // doesn't feel deterministic between sessions.
  const startIndexRef = useRef(null);
  useEffect(() => {
    if (startIndexRef.current === null && announcements.length > 0) {
      startIndexRef.current = Math.floor(Math.random() * announcements.length);
      setCurrentIndex(startIndexRef.current);
    }
  }, [announcements.length]);

  // Auto-cycle. Paused while a stub-build is in flight (the user is
  // mid-action and the headline is showing "Building …").
  useEffect(() => {
    if (announcements.length <= 1) return undefined;
    if (building) return undefined;
    const t = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % announcements.length);
    }, ANNOUNCEMENT_CYCLE_MS);
    return () => clearInterval(t);
  }, [announcements.length, building]);

  // Clamp the index in case the announcement list shrank under us.
  const safeIndex = announcements.length > 0
    ? currentIndex % announcements.length
    : 0;
  const current = announcements[safeIndex];

  const onStubTap = async (entry) => {
    if (!entry?.stub) return;
    if (building) return;
    setBuilding({ stubId: entry.stub.id, title: entry.stub.title });
    try {
      const { recipe } = await cookStub(entry.stub, { pantry, saveRecipe });
      onCookRecipe?.(recipe);
    } catch (e) {
      console.warn("[ReadyToCookBanner] cookStub failed:", e?.message || e);
    } finally {
      setBuilding(null);
    }
  };

  if (!current) return null;

  // Derive headline / subline / tap action for the visible card.
  let headline, sublineLabel, emoji, onTap, isBuilding = false;
  if (current.kind === "user-recipe") {
    emoji = current.recipe.emoji || "🍳";
    headline = `You have the ingredients to make ${current.recipe.title}!`;
    sublineLabel = "Tap to get cookin'";
    onTap = () => onCookRecipe?.(current.recipe);
  } else {
    const stub = current.stub;
    const occ = current.occasion;
    const occLabel = occ?.def?.label || "Today's pick";
    const daysOut = occ?.daysOut;
    const occChip = occ?.kind === "seasonal" && daysOut != null
      ? (daysOut === 0 ? `${occLabel} · today`
         : daysOut === 1 ? `${occLabel} · tomorrow`
         : `${occLabel} · in ${daysOut} days`)
      : occLabel;
    emoji = stub.emoji || "🍳";
    headline = `Try ${stub.title}`;
    sublineLabel = occChip;
    isBuilding = building?.stubId === stub.id;
    onTap = () => onStubTap(current);
  }

  // Cycle pip indicator — small dots under the card showing
  // position in the rotation when 2+ announcements exist.
  const showPips = announcements.length > 1;

  return (
    <div style={{ margin: "20px 16px 8px" }}>
      <button
        onClick={onTap}
        disabled={isBuilding}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "16px 18px",
          background: "linear-gradient(135deg, #2F8F83 0%, #1F6E64 100%)",
          border: "none",
          borderRadius: 16,
          boxShadow: "0 4px 18px -6px rgba(31, 110, 100, 0.4)",
          cursor: isBuilding ? "wait" : "pointer",
          textAlign: "left",
          color: "#FFFFFF",
          opacity: isBuilding ? 0.85 : 1,
          overflow: "hidden",
          minHeight: 86,
        }}
      >
        {/* AnimatePresence + key on the card body → fade-and-slide
            transition between announcements. mode="wait" makes the
            outgoing card finish exiting before the incoming card
            enters, so the layout doesn't jump. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current.key + (isBuilding ? ":building" : "")}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 14,
              minWidth: 0,
            }}
          >
            <div style={{
              fontSize: 36,
              flexShrink: 0,
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
            }}>
              {emoji}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: FONT.serif,
                fontSize: 18,
                fontStyle: "italic",
                lineHeight: 1.25,
                marginBottom: 4,
              }}>
                {isBuilding ? `Building ${building.title}…` : headline}
              </div>
              <div style={{
                fontFamily: FONT.mono,
                fontSize: 10,
                letterSpacing: "0.12em",
                opacity: 0.85,
                textTransform: "uppercase",
              }}>
                {isBuilding ? "Hang on a sec" : sublineLabel}
              </div>
            </div>
            <div style={{
              fontFamily: FONT.mono,
              fontSize: 18,
              opacity: 0.9,
              flexShrink: 0,
            }}>
              {isBuilding ? "…" : "→"}
            </div>
          </motion.div>
        </AnimatePresence>
      </button>
      {showPips && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: 5,
          marginTop: 6,
        }}>
          {announcements.map((a, i) => (
            <span
              key={a.key}
              style={{
                width: i === safeIndex ? 12 : 4,
                height: 4,
                borderRadius: 2,
                background: i === safeIndex ? "#2F8F83" : "rgba(60,40,18,0.25)",
                transition: "width 0.3s ease, background 0.3s ease",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Activity row — either a cook event or a badge earn ───────────────────────
function ActivityRow({ item, nameFor, avatarFor, badgeById, onCookTap, onActorTap, viewerId }) {
  const rowStyle = useTileRowStyle();
  const actorName = nameFor ? nameFor(item.actorId) : "Someone";
  const isSelf = item.actorId === viewerId;
  const displayName = isSelf ? "You" : actorName;
  const avatarInitial = (actorName || "?")[0]?.toUpperCase() || "?";
  const actorAvatar = avatarFor ? avatarFor(item.actorId) : null;

  if (item.kind === "cook") {
    const p = item.payload;
    const face = RATING_EMOJI[p.rating] || "";
    const fresh = isFresh(item.ts);
    return (
      <button
        onClick={() => onCookTap?.(p.cookLogId)}
        style={{
          ...rowStyle,
          ...(fresh ? FRESH_ROW_OVERLAY : null),
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
        }}
      >
        <Avatar name={actorName} initial={avatarInitial} imageUrl={actorAvatar}
          onClick={(e) => { e.stopPropagation(); onActorTap?.(item.actorId); }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT.sans, fontSize: 14, color: COLOR.ink, lineHeight: 1.35 }}>
            <b style={{ fontWeight: 500 }}>{displayName}</b>
            {" cooked "}
            <span style={{ fontFamily: FONT.serif, fontStyle: "italic" }}>
              {p.recipeEmoji} {p.recipeTitle}
            </span>
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.muted, marginTop: 3, display: "flex", alignItems: "center", gap: 6 }}>
            <span>{face}</span>
            <span>{timeAgo(item.ts).toUpperCase()}</span>
            <span style={{ color: COLOR.sub }}>·</span>
            <span>+{p.xp} XP</span>
          </div>
        </div>
        <span style={{ fontFamily: FONT.mono, fontSize: 14, color: COLOR.gold, flexShrink: 0 }}>→</span>
      </button>
    );
  }

  if (item.kind === "badge") {
    const badge = badgeById.get(item.payload.badgeId);
    // If the catalog hasn't loaded yet (or the badge was deleted), skip
    // rendering rather than showing a broken row.
    if (!badge) return null;
    return (
      <button
        onClick={() => onActorTap?.(item.actorId)}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          background: COLOR.goldDeep, border: `1px solid ${badge.color}33`, borderRadius: 14,
          padding: "12px 14px", cursor: "pointer", textAlign: "left", width: "100%",
          boxShadow: badge.maxAwards === 1 ? `0 0 24px ${badge.color}22` : "none",
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: COLOR.goldDeep, border: `1px solid ${badge.color}55`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, padding: 4,
        }}>
          <img
            src={badge.iconPath}
            alt={badge.name}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
            onError={(e) => { e.currentTarget.outerHTML = '<span style="font-size:20px">🏅</span>'; }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT.sans, fontSize: 14, color: COLOR.ink, lineHeight: 1.35 }}>
            <b style={{ fontWeight: 500 }}>{displayName}</b>
            {" earned "}
            <span style={{ fontFamily: FONT.serif, fontStyle: "italic", color: badge.color }}>
              {badge.name}
            </span>
            {badge.maxAwards === 1 && (
              <span style={{ marginLeft: 6, fontFamily: FONT.mono, fontSize: 9, color: badge.color, letterSpacing: "0.08em" }}>
                🥇 1/1
              </span>
            )}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.muted, marginTop: 3 }}>
            {timeAgo(item.ts).toUpperCase()}
          </div>
        </div>
        <span style={{ fontFamily: FONT.mono, fontSize: 14, color: badge.color, flexShrink: 0 }}>→</span>
      </button>
    );
  }

  return null;
}

function Avatar({ name, initial, imageUrl, onClick }) {
  // Google avatar URLs occasionally 403 after rotation. Fall back to
  // the initial-letter tile on load error so we never render a broken
  // image icon.
  const [imgBroken, setImgBroken] = useState(false);
  const showImage = imageUrl && !imgBroken;
  return (
    <button
      onClick={onClick}
      title={name}
      style={{
        width: 48, height: 48, borderRadius: 24,
        background: showImage ? "transparent" : avatarColor(name),
        color: COLOR.gold,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: FONT.serif, fontSize: 22, fontWeight: 500,
        flexShrink: 0, border: "none", cursor: "pointer", padding: 0,
        overflow: "hidden",
      }}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt={name || ""}
          onError={() => setImgBroken(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          referrerPolicy="no-referrer"
        />
      ) : initial}
    </button>
  );
}

// ProfilePill removed — duplicated UserProfile's quick stats. Diet,
// skill level, and goal still live there; Home shouldn't mirror.

// ── Focus tier rendering ────────────────────────────────────────
// buildFocusCard turns one tier descriptor into a renderable card
// shape. Pure function so FocusCarousel can map over the tier list
// and render each as a swipeable page.
function buildFocusCard(focus, handlers) {
  const { onOpenAIRecipe, onOpenCalendar, onOpenKitchen, onRestock } = handlers;
  if (focus?.kind === "almost-time") {
    const m = focus.meal;
    const at = focus.scheduledFor;
    const hh = at.getHours();
    const mm = String(at.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "pm" : "am";
    const hh12 = hh % 12 || 12;
    return {
      accent: COLOR.gold,
      kicker: "Almost time",
      title: m.recipe_title || m.recipeTitle || "Your meal",
      blurb: `Scheduled for ${hh12}:${mm} ${ampm}. Open the calendar to start cooking.`,
      onClick: () => onOpenCalendar?.(),
    };
  }
  if (focus?.kind === "use-this-up") {
    const it = focus.item;
    const canon = it.canonicalId ? findIngredient(it.canonicalId) : null;
    const name = canon?.name || it.name || "Something";
    const hours = Math.max(0, Math.round((focus.expiresAt.getTime() - Date.now()) / 3600000));
    const when = hours <= 24 ? `Expires in ${hours}h` : `Expires in ${Math.round(hours / 24)}d`;
    return {
      accent: COLOR.rose,
      kicker: "Use this up",
      title: name,
      blurb: `${when}. Cook something around it before it goes bad.`,
      onClick: () => onOpenAIRecipe?.({ anchor: name }),
    };
  }
  if (focus?.kind === "restock") {
    const names = focus.missing.map((m) => m.name);
    let title;
    if (names.length === 1) title = `You're out of ${names[0]}`;
    else if (names.length === 2) title = `You're out of ${names[0]} and ${names[1]}`;
    else title = `You're out of ${names[0]}, ${names[1]}, and ${names.length - 2} more`;
    return {
      accent: COLOR.sky,
      kicker: "Restock",
      title,
      blurb: "Tap to add every missing staple to your shopping list — one tap, you're ready to shop.",
      onClick: () => onRestock?.(focus.missing),
    };
  }
  if (focus?.kind === "plan-tomorrow") {
    return {
      accent: COLOR.plum,
      kicker: "Plan tomorrow",
      title: focus.stocked
        ? "Nothing on tomorrow's menu"
        : "Tomorrow's a blank slate",
      blurb: focus.stocked
        ? "Pick a recipe and slot it into the calendar — your kitchen's already stocked."
        : "Pick something to cook tomorrow — we'll start the shopping list for you.",
      onClick: () => onOpenCalendar?.(),
    };
  }
  // Default — copy bends to first-time users with no staples flagged.
  if (focus?.kind === "ai-suggest" && focus?.hasStaples === false) {
    return {
      accent: COLOR.plum,
      kicker: "Tip",
      title: "Mark items as staples",
      blurb: "Tap the star on any item to flag it as a staple — we'll let you know when one runs low.",
      onClick: () => onOpenKitchen?.(),
    };
  }
  return {
    accent: COLOR.plum,
    kicker: "Idea",
    title: "Pick something to make",
    blurb: "Let Claude draft a recipe using what's already on your shelves.",
    onClick: () => onOpenAIRecipe?.(),
  };
}

// FocusCarousel — swipeable + auto-rotating list of every valid
// nudge from the cascade. Solves "the card got stuck on use-this-up
// and never showed me the planning gap" by surfacing all valid tiers
// as separate pages.
//
// Behavior:
//   - Horizontal scroll-snap pages, one card per tier.
//   - Pagination dots below; tap a dot to jump.
//   - Auto-rotates every AUTO_ROTATE_MS. Pauses on user interaction
//     (touch / scroll / dot tap) and resumes after RESUME_DELAY_MS
//     of idle so the user has time to read.
//   - With one tier, renders a static card (no carousel chrome).
const AUTO_ROTATE_MS  = 6000;   // ~6s per page
const RESUME_DELAY_MS = 12000;  // wait 12s after touch before resuming

function FocusCarousel({ tiers, onOpenAIRecipe, onOpenCalendar, onOpenKitchen, onRestock }) {
  const safeTiers = Array.isArray(tiers) && tiers.length > 0 ? tiers : [];
  const handlers = useMemo(
    () => ({ onOpenAIRecipe, onOpenCalendar, onOpenKitchen, onRestock }),
    [onOpenAIRecipe, onOpenCalendar, onOpenKitchen, onRestock],
  );
  const cards = useMemo(
    () => safeTiers.map((t) => ({ tier: t, ...buildFocusCard(t, handlers) })),
    [safeTiers, handlers],
  );

  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  // Pause flag + last-interaction timestamp drive auto-rotate gating.
  const lastInteractRef = useRef(0);

  // Keep activeIdx valid when the tier set changes (tier disappears
  // because the underlying state shifted: item cooked, meal scheduled,
  // staple stocked). Clamp to a valid range; default to 0.
  useEffect(() => {
    if (activeIdx >= cards.length) setActiveIdx(Math.max(0, cards.length - 1));
  }, [cards.length, activeIdx]);

  // Track scroll-driven page changes (user swiped). Updates dots and
  // resets the auto-rotate idle clock.
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activeIdx) {
      setActiveIdx(idx);
      lastInteractRef.current = Date.now();
    }
  };

  const goToPage = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: "smooth" });
    setActiveIdx(idx);
    lastInteractRef.current = Date.now();
  };

  // Auto-rotate. Re-checks every second; only advances when
  // (a) more than one page exists, and (b) the user hasn't touched
  // it in RESUME_DELAY_MS.
  useEffect(() => {
    if (cards.length <= 1) return undefined;
    const id = setInterval(() => {
      const idleFor = Date.now() - lastInteractRef.current;
      if (idleFor < RESUME_DELAY_MS) return;
      const next = (activeIdx + 1) % cards.length;
      const el = scrollerRef.current;
      if (el) el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
      setActiveIdx(next);
    }, AUTO_ROTATE_MS);
    return () => clearInterval(id);
  }, [cards.length, activeIdx]);

  if (cards.length === 0) return null;

  // Single-tier fast path — no carousel chrome, no rotation.
  if (cards.length === 1) {
    const c = cards[0];
    return (
      <MCMBigCard
        flat
        accent={c.accent}
        kicker={c.kicker}
        title={c.title}
        blurb={c.blurb}
        onClick={c.onClick}
      />
    );
  }

  return (
    <div>
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        onTouchStart={() => { lastInteractRef.current = Date.now(); }}
        style={{
          display: "flex",
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {cards.map((c) => (
          <div
            key={c.tier.kind}
            style={{
              flexShrink: 0,
              width: "100%",
              scrollSnapAlign: "start",
              scrollSnapStop: "always",
            }}
          >
            <MCMBigCard
              flat
              accent={c.accent}
              kicker={c.kicker}
              title={c.title}
              blurb={c.blurb}
              onClick={c.onClick}
            />
          </div>
        ))}
      </div>
      {/* Pagination dots — active is wider/gold; tap to jump. */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 14 }}>
        {cards.map((c, i) => (
          <button
            key={c.tier.kind}
            type="button"
            onClick={() => goToPage(i)}
            aria-label={`Show nudge ${i + 1} of ${cards.length}`}
            style={{
              width: i === activeIdx ? 22 : 6,
              height: 6,
              borderRadius: 3,
              background: i === activeIdx ? COLOR.gold : COLOR.edge,
              border: "none",
              padding: 0,
              cursor: "pointer",
              transition: "width 220ms cubic-bezier(0.23, 1, 0.32, 1), background 220ms cubic-bezier(0.23, 1, 0.32, 1)",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── SnapshotPanel — swipeable 4-page activity snapshot ──────────
// Top: a 4-segment Forma control with a sliding accent pill that
// follows the active page. Body: a horizontal CSS scroll-snap
// container, one full-width page per category. Active page tracks
// scrollLeft; tapping a segment programmatically scrolls.
// Calendar first per user direction — what's coming up matters more
// than what just happened. The remaining categories follow the
// activity-density order (cooks happen most often, then kitchen
// changes, then recipe authoring).
const SNAPSHOT_PAGES = [
  { id: "calendar", label: "Calendar" },
  { id: "cooks",    label: "Cooks" },
  { id: "kitchen",  label: "Kitchen" },
  { id: "recipes",  label: "Recipes" },
];

function SnapshotPanel({
  snapshot, familyLoading, familyIds,
  nameFor, avatarFor, viewerId, findUserRecipe,
  onCookTap, onActorTap, onOpenKitchen, onOpenCalendar, onScheduledTap,
  // Conductor props — when present, the Cooks sub-tab renders the
  // FamilyKitchenView (active cooks + planned meals + plan-a-meal)
  // ABOVE the historical CooksList.
  activeCooks = [],
  mealPlans = [],
  fireMealPlanRecipe = null,
  onJumpToCook = null,
  onOpenMealPlanBuilder = null,
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef(null);
  const pageRefs = useRef([]);

  // Track active page on user-driven scroll. Snap-to-page guarantees
  // scrollLeft is a multiple of page width, so floor-divide gives
  // the index. Throttled via rAF so a flick doesn't spam state.
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activeIdx) setActiveIdx(idx);
  };

  const goToPage = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: "smooth" });
    setActiveIdx(idx);
  };

  return (
    <div style={{ marginTop: 28 }}>
      {/* Segmented control */}
      <div style={{
        margin: "0 20px",
        padding: 4,
        background: COLOR.deep,
        border: `1px solid ${COLOR.edge}`,
        borderRadius: 999,
        display: "flex",
        gap: 2,
        position: "relative",
      }}>
        {SNAPSHOT_PAGES.map((p, i) => {
          const active = i === activeIdx;
          return (
            <button
              key={p.id}
              onClick={() => goToPage(i)}
              style={{
                flex: 1,
                padding: "8px 10px",
                background: active ? COLOR.ground : "transparent",
                border: "none",
                borderRadius: 999,
                fontFamily: FONT.forma,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: active ? COLOR.ink : COLOR.muted,
                cursor: "pointer",
                transition: "background 0.18s ease, color 0.18s ease",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Swipeable pages */}
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        style={{
          marginTop: 14,
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          scrollbarWidth: "none",
        }}
      >
        <SnapshotPage ref={(el) => (pageRefs.current[0] = el)}>
          <CalendarList
            meals={snapshot.calendar}
            total={snapshot.calendarTotal}
            nameFor={nameFor}
            avatarFor={avatarFor}
            viewerId={viewerId}
            findUserRecipe={findUserRecipe}
            onSeeMore={onOpenCalendar}
            onScheduledTap={onScheduledTap}
          />
        </SnapshotPage>
        <SnapshotPage ref={(el) => (pageRefs.current[1] = el)}>
          {/* Live cooks (lock-screen-shape cards) at the top of the
              Cooks sub-tab, with past history below. */}
          {activeCooks?.length > 0 ? (
            <div style={{ marginBottom: 18 }}>
              <FamilyKitchenView
                cooks={activeCooks || []}
                currentUserId={viewerId}
                avatarFor={avatarFor}
                onJumpToCook={onJumpToCook}
              />
            </div>
          ) : null}
          <CooksList
            cooks={snapshot.cooks}
            total={snapshot.cooksTotal}
            loading={snapshot.loading || familyLoading}
            familyIds={familyIds}
            nameFor={nameFor}
            avatarFor={avatarFor}
            viewerId={viewerId}
            onCookTap={onCookTap}
            onActorTap={onActorTap}
          />
        </SnapshotPage>
        <SnapshotPage ref={(el) => (pageRefs.current[2] = el)}>
          <KitchenList
            items={snapshot.kitchen}
            total={snapshot.kitchenTotal}
            nameFor={nameFor}
            avatarFor={avatarFor}
            viewerId={viewerId}
            onSeeMore={onOpenKitchen}
          />
        </SnapshotPage>
        <SnapshotPage ref={(el) => (pageRefs.current[3] = el)}>
          <RecipesList
            recipes={snapshot.recipes}
            total={snapshot.recipesTotal}
            nameFor={nameFor}
            avatarFor={avatarFor}
            viewerId={viewerId}
          />
        </SnapshotPage>
      </div>
    </div>
  );
}

// One snap-page slot. flex-shrink: 0 + scroll-snap-align: start
// keeps each page exactly viewport-width and snaps when released.
const SnapshotPage = forwardRef(function SnapshotPage({ children }, ref) {
  return (
    <div
      ref={ref}
      style={{
        flexShrink: 0,
        width: "100%",
        padding: "0 20px",
        scrollSnapAlign: "start",
        scrollSnapStop: "always",
      }}
    >
      {children}
    </div>
  );
});

// ── List components — one per category ─────────────────────────
const SHOW_MORE_STYLE = {
  display: "block",
  marginTop: 10,
  padding: "10px 14px",
  background: "transparent",
  border: `1px solid ${COLOR.edge}`,
  borderRadius: 10,
  color: COLOR.muted,
  fontFamily: FONT.forma,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  textAlign: "center",
  cursor: "pointer",
  width: "100%",
};

// Static fallback — most consumers should call useEmptyStyle()
// instead so the placeholder tracks the active theme like the rest
// of the snapshot tiles.
const EMPTY_STYLE = {
  padding: "26px 20px",
  textAlign: "center",
  background: COLOR.ground,
  border: `1px dashed ${COLOR.edge}`,
  borderRadius: 24,
  fontFamily: FONT.forma,
  fontSize: 12,
  color: COLOR.muted,
  lineHeight: 1.5,
};
function useEmptyStyle() {
  const { theme } = useMCMTheme();
  return useMemo(() => ({
    padding: "26px 20px",
    textAlign: "center",
    background: theme.color.glassFillLite,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: `1px dashed ${theme.color.glassBorder}`,
    borderRadius: 24,
    fontFamily: FONT.forma,
    fontSize: 12,
    color: theme.color.inkMuted || COLOR.muted,
    lineHeight: 1.5,
  }), [theme]);
}

function CooksList({ cooks, total, loading, familyIds, nameFor, avatarFor, viewerId, onCookTap, onActorTap }) {
  const emptyStyle = useEmptyStyle();
  if (loading) return <div style={emptyStyle}>Loading cooks…</div>;
  if (!cooks || cooks.length === 0) {
    return (
      <div style={emptyStyle}>
        {familyIds.length === 0
          ? "Add family in Settings to see what they cook."
          : "Your first cook will show up here."}
      </div>
    );
  }
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {cooks.map((item, i) => (
          <ActivityRow
            key={`${item.kind}-${item.payload?.cookLogId || i}`}
            item={item}
            nameFor={nameFor}
            avatarFor={avatarFor}
            badgeById={new Map()}
            onCookTap={onCookTap}
            onActorTap={onActorTap}
            viewerId={viewerId}
          />
        ))}
      </div>
      {total > cooks.length && (
        <div style={SHOW_MORE_STYLE}>Show more · {total - cooks.length} more</div>
      )}
    </>
  );
}

function KitchenList({ items, total, nameFor, avatarFor, viewerId, onSeeMore }) {
  const rowStyle = useTileRowStyle();
  const text = useTileRowTextStyles();
  const emptyStyle = useEmptyStyle();
  if (!items || items.length === 0) {
    return <div style={emptyStyle}>No new kitchen additions in the last two weeks.</div>;
  }
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it) => {
          const isSelf = it.user_id === viewerId;
          const actorName = nameFor?.(it.user_id) || "Family";
          const actorImg = avatarFor?.(it.user_id) || null;
          const displayName = isSelf ? "You" : actorName;
          const itemName = it.brand ? `${it.brand} ${it.name}` : it.name;
          const fresh = isFresh(it.created_at);
          return (
            <div key={it.id} style={fresh ? { ...rowStyle, ...FRESH_ROW_OVERLAY } : rowStyle}>
              <Avatar name={actorName} initial={(actorName || "?")[0]?.toUpperCase()} imageUrl={actorImg} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={text.narration}>
                  <b style={{ fontWeight: 500 }}>{displayName}</b>
                  {" added "}
                  <span style={text.itemInline}>{itemName}</span>
                </div>
                <div style={text.meta}>{timeAgo(it.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {(total > items.length || onSeeMore) && (
        <button onClick={onSeeMore} style={SHOW_MORE_STYLE}>
          {total > items.length ? `Show all · ${total - items.length} more` : "Open kitchen"}
        </button>
      )}
    </>
  );
}

function RecipesList({ recipes, total, nameFor, avatarFor, viewerId }) {
  const rowStyle = useTileRowStyle();
  const text = useTileRowTextStyles();
  const emptyStyle = useEmptyStyle();
  if (!recipes || recipes.length === 0) {
    return <div style={emptyStyle}>No new recipes saved in the last month.</div>;
  }
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {recipes.map((r) => {
          const isSelf = r.user_id === viewerId;
          const actorName = nameFor?.(r.user_id) || "Family";
          const actorImg = avatarFor?.(r.user_id) || null;
          const displayName = isSelf ? "You" : actorName;
          const title = r.recipe?.title || r.slug || "Untitled";
          // Source-aware verb: "saved" for paste/custom, "drafted"
          // for AI, "added" as default.
          const verb = r.source === "ai" ? "drafted" : r.source === "custom" ? "wrote" : "saved";
          const fresh = isFresh(r.created_at);
          return (
            <div key={r.id} style={fresh ? { ...rowStyle, ...FRESH_ROW_OVERLAY } : rowStyle}>
              <Avatar name={actorName} initial={(actorName || "?")[0]?.toUpperCase()} imageUrl={actorImg} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={text.narration}>
                  <b style={{ fontWeight: 500 }}>{displayName}</b>
                  {" "}{verb}{" "}
                  <span style={text.itemInline}>{title}</span>
                </div>
                <div style={text.meta}>{timeAgo(r.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {total > recipes.length && (
        <div style={SHOW_MORE_STYLE}>Show all · {total - recipes.length} more</div>
      )}
    </>
  );
}

function CalendarList({ meals, total, nameFor, avatarFor, viewerId, findUserRecipe, onSeeMore, onScheduledTap }) {
  const rowStyle = useTileRowStyle();
  const text = useTileRowTextStyles();
  const emptyStyle = useEmptyStyle();
  if (!meals || meals.length === 0) {
    return <div style={emptyStyle}>Nothing scheduled for the next month.</div>;
  }
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {meals.map((m) => {
          const at = new Date(m.scheduled_for);
          const dateLabel = at.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
          const hh = at.getHours();
          const mm = String(at.getMinutes()).padStart(2, "0");
          const ampm = hh >= 12 ? "pm" : "am";
          const hh12 = hh % 12 || 12;
          const isSelf = m.user_id === viewerId;
          const actorName = nameFor?.(m.user_id) || "Family";
          const actorImg = avatarFor?.(m.user_id) || null;
          const displayName = isSelf ? "You" : actorName;
          // Resolve title via the same cascade Plan/CalendarDay use:
          // bundled recipe → user-authored row → prettified slug. The
          // raw `recipe_title`/`recipeTitle` fields don't exist on
          // scheduled_meals, so the previous "Meal" fallback fired
          // every time. Pass recipe_user_id so family-scheduled meals
          // resolve against the author's user_recipes row, not the
          // viewer's.
          const resolved = m.recipe_slug
            ? findRecipe(m.recipe_slug, findUserRecipe, m.recipe_user_id)
            : null;
          const title = resolved?.title
            || m.recipe_title
            || m.recipeTitle
            || prettifySlug(m.recipe_slug)
            || "Meal";
          const fresh = isFresh(m.created_at);
          const tappable = !!onScheduledTap;
          const baseStyle = fresh ? { ...rowStyle, ...FRESH_ROW_OVERLAY } : rowStyle;
          const buttonStyle = { ...baseStyle, cursor: "pointer", textAlign: "left", width: "100%" };
          return tappable ? (
            <button
              key={m.id}
              onClick={() => onScheduledTap?.(m)}
              style={buttonStyle}
            >
              <Avatar name={actorName} initial={(actorName || "?")[0]?.toUpperCase()} imageUrl={actorImg} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={text.narration}>
                  <b style={{ fontWeight: 500 }}>{displayName}</b>
                  {" scheduled "}
                  <span style={text.itemInline}>{title}</span>
                </div>
                <div style={text.meta}>{dateLabel} · {hh12}:{mm}{ampm}</div>
              </div>
              <span style={{ fontFamily: FONT.mono, fontSize: 14, color: COLOR.gold, flexShrink: 0 }}>→</span>
            </button>
          ) : (
            <div key={m.id} style={baseStyle}>
              <Avatar name={actorName} initial={(actorName || "?")[0]?.toUpperCase()} imageUrl={actorImg} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={text.narration}>
                  <b style={{ fontWeight: 500 }}>{displayName}</b>
                  {" scheduled "}
                  <span style={text.itemInline}>{title}</span>
                </div>
                <div style={text.meta}>{dateLabel} · {hh12}:{mm}{ampm}</div>
              </div>
            </div>
          );
        })}
      </div>
      {(total > meals.length || onSeeMore) && (
        <button onClick={onSeeMore} style={SHOW_MORE_STYLE}>
          {total > meals.length ? `Show all · ${total - meals.length} more` : "Open calendar"}
        </button>
      )}
    </>
  );
}

// Static fallback ROW_STYLE — only used by code paths that can't
// call hooks. Real rendering goes through useTileRowStyle() below.
const ROW_STYLE = {
  display: "flex", alignItems: "center", gap: 12,
  background: COLOR.ground,
  border: `1px solid ${COLOR.edge}`,
  borderRadius: 24,
  padding: "14px 16px",
};
const ROW_TITLE_STYLE = {
  fontFamily: FONT.itemTitle,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 16,
  lineHeight: 1.2,
  color: COLOR.ink,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const ROW_META_STYLE = {
  fontFamily: FONT.forma,
  fontSize: 11,
  color: COLOR.muted,
  marginTop: 3,
};
const ROW_NARRATION_STYLE = {
  fontFamily: FONT.forma,
  fontSize: 14,
  color: COLOR.ink,
  lineHeight: 1.35,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const ROW_ITEM_INLINE_STYLE = {
  fontFamily: FONT.itemTitle,
  fontStyle: "italic",
  fontWeight: 500,
};
